import React from "react";
import classes from "./searchResultRow.module.scss";
import {
    CATEGORY_CONFIG,
    isWeakChip,
    MATCH_CONTEXT_ICON,
    MATCH_CONTEXT_TITLE,
    matchChips,
    SearchResultItem,
} from "../../constants/searchCategories";

/**
 * Wrap every case-insensitive occurrence of `query` in a <mark>.
 * @param text - The text to display.
 * @param query - What the user typed.
 */
export const highlightMatch = (text: string, query: string): React.ReactNode => {
    const needle = query.trim();
    if (!needle || !text) return text;
    const escaped = needle.replace(/[.*+?^=!:${}()|[\]/\\]/g, "\\$&");
    const regex = new RegExp(`(${escaped})`, "gi");
    return text.split(regex).map((part, index) =>
        part.toLowerCase() === needle.toLowerCase()
            ? <mark key={index} className={classes.highlight}>{part}</mark>
            : part,
    );
};

interface MatchChipListProps {
    item: Pick<SearchResultItem, "matchContext" | "geoContext">;
    /** Cap for one-line layouts; the rest collapse into "+N". */
    max?: number;
}

/** The "why this matched" chips (place, service, rating…) for one result. */
export const MatchChipList: React.FC<MatchChipListProps> = ({ item, max }) => {
    const chips = matchChips(item);
    if (chips.length === 0) return null;
    const shown = max ? chips.slice(0, max) : chips;
    const hidden = chips.length - shown.length;
    return (
        <>
            {shown.map((chip, index) => (
                <span
                    key={`${chip.kind}-${index}`}
                    className={`${classes.chip} ${isWeakChip(chip.kind) ? classes.chipWeak : ""}`}
                    title={MATCH_CONTEXT_TITLE[chip.kind]}
                >
                    <i className={`bi ${MATCH_CONTEXT_ICON[chip.kind]}`} aria-hidden="true" />
                    <span className={classes.chipLabel}>{chip.label}</span>
                </span>
            ))}
            {hidden > 0 && (
                <span className={`${classes.chip} ${classes.chipWeak}`} title={chips.slice(shown.length).map((c) => c.label).join(" · ")}>
                    <span className={classes.chipLabel}>+{hidden}</span>
                </span>
            )}
        </>
    );
};

interface SearchResultRowProps {
    item: SearchResultItem;
    query: string;
    /** compact = header dropdown; full = the /search page. */
    variant?: "compact" | "full";
    isSelected?: boolean;
    onOpen?: (item: SearchResultItem) => void;
    anchorRef?: (element: HTMLAnchorElement | null) => void;
}

/**
 * One search hit: type tile, highlighted name + reference, why it matched,
 * and the contact details. Opens the record in a new tab.
 */
const SearchResultRow: React.FC<SearchResultRowProps> = ({ item, query, variant = "full", isSelected = false, onOpen, anchorRef }) => {
    const config = CATEGORY_CONFIG[item.type];
    const name = item.name || item.referenceNumber || "(untitled)";
    const reference = item.referenceNumber && item.referenceNumber !== item.name ? item.referenceNumber : null;
    const hasMeta = matchChips(item).length > 0 || !!item.parent || !!item.email || !!item.phoneNumber;

    return (
        <a
            ref={anchorRef}
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => onOpen?.(item)}
            className={`${classes.row} ${variant === "compact" ? classes.compact : ""} ${isSelected ? classes.selected : ""} ${item.inactive ? classes.inactive : ""}`}
            title={`Open ${name} in a new tab`}
        >
            <span className={classes.tile} aria-hidden="true">
                <i className={`bi ${config?.icon ?? "bi-search"}`} />
            </span>
            <span className={classes.body}>
                <span className={classes.titleLine}>
                    <span className={classes.name}>{highlightMatch(name, query)}</span>
                    {reference && <span className={classes.reference}>{highlightMatch(reference, query)}</span>}
                    {item.inactive && (
                        <span className={classes.inactiveBadge} title="This record is inactive (deleted)">Inactive</span>
                    )}
                </span>
                {hasMeta && (
                    <span className={classes.metaLine}>
                        <MatchChipList item={item} />
                        {item.parent && (
                            <span className={classes.meta}>
                                <i className="bi bi-building" aria-hidden="true" />
                                <span className={classes.metaText}>{highlightMatch(item.parent, query)}</span>
                            </span>
                        )}
                        {item.email && (
                            <span className={classes.meta}>
                                <i className="bi bi-envelope" aria-hidden="true" />
                                <span className={classes.metaText}>{highlightMatch(item.email, query)}</span>
                            </span>
                        )}
                        {item.phoneNumber && (
                            <span className={classes.meta}>
                                <i className="bi bi-telephone" aria-hidden="true" />
                                <span className={classes.metaText}>{highlightMatch(item.phoneNumber, query)}</span>
                            </span>
                        )}
                    </span>
                )}
                {item.note && (
                    <span className={classes.note}>
                        <i className="bi bi-sticky" aria-hidden="true" />
                        <span className={classes.noteText}>{highlightMatch(item.note, query)}</span>
                    </span>
                )}
            </span>
            <i className={`bi bi-box-arrow-up-right ${classes.openIcon}`} aria-hidden="true" />
        </a>
    );
};

export default SearchResultRow;
